import { FormikProps } from 'formik'
import { pick } from 'lodash'
import * as yup from 'yup'

import { DEFAULT_PALETTE } from '~/app/providers/ui/theme'
import { EMAIL_REGEX, HEX_COLOR_REGEX } from '~/constants'
import { getStringListFromArray } from '~/features/campaigns/utils'
import {
  ECustomerAuthType,
  EPartnerType,
  ESiteImagesTypes,
  partnerKeys,
  TBrandColor,
  TPartner,
  TPartnerGeneralSettingsForm,
  TPartnerPricing,
  TSiteImages,
} from '~/features/partners/models'
import { convertPaletteObjectToBrandColors } from '~/features/partners/utils'
import { EChatbotTheme } from '~/features/settings/models'
import { EServerPath } from '~/models/api'
import { ECognitoGroups } from '~/models/auth'

export const queryKeysPartners = {
  partners: () => [EServerPath.partners],
  partner: (id: string) => [EServerPath.partner, { partnerId: id }],
}

export const partnerValidationSchema = yup.object<TPartner>({
  partnerName: yup.string().required('Partner name is required'),
  domain: yup.string().required('Domain is required'),
  trackingEmail: yup
    .string()
    .matches(EMAIL_REGEX, 'Invalid email')
    .required('Tracking email is required'),
  collectionEmail: yup
    .string()
    .matches(EMAIL_REGEX, 'Invalid email')
    .required('Collection email is required'),
  showBrandingToggle: yup.boolean().optional(),
  showCharacterToggle: yup.boolean().optional(),
  showEmbeddedChat: yup.boolean().optional(),
  defaultAgentName: yup.string().optional(),
  defaultService: yup.string().optional(),
  brandUIColors: yup
    .array()
    .of(
      yup.object({
        id: yup.string().optional(),
        type: yup.string().required(),
        color: yup
          .string()
          .matches(
            HEX_COLOR_REGEX,
            'Color must be a valid 6-character hex code (e.g. #FF0000)',
          )
          .required('Color is required'),
      }),
    )
    .optional()
    .nullable(),
  customHeaderLinks: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        label: yup.string().required(),
        url: yup.string().required(),
      }),
    )
    .nullable(),
  socialLinks: yup
    .array()
    .of(
      yup.object({
        id: yup.string().optional(),
        platform: yup.string().optional(),
        url: yup.string().url('Invalid URL format').optional(),
      }),
    )
    .nullable(),
  chatAgentId: yup.string().optional(),
  billingEmail: yup.string().matches(EMAIL_REGEX, 'Invalid email').optional(),
  supportEmail: yup.string().matches(EMAIL_REGEX, 'Invalid email').optional(),
  notificationEmail: yup
    .string()
    .matches(EMAIL_REGEX, 'Invalid email')
    .optional(),
  emailDomain: yup.string(),
  partnerType: yup
    .mixed<EPartnerType>()
    .oneOf(Object.values(EPartnerType))
    .optional(),
  pricing: yup
    .array()
    .of(
      yup.object({
        id: yup.string().optional(),
        tier: yup
          .mixed<ECognitoGroups>()
          .oneOf([
            ECognitoGroups.customerBasicFreeTrialPlan,
            ECognitoGroups.customerFreePlan,
            ECognitoGroups.customerBasicPlan,
            ECognitoGroups.customerPlusPlan,
          ])
          .required('Tier is required'),
        stripeProductId: yup.string().required('Stripe Product Id is required'),
        stripePriceId: yup.string().required('Stripe Price Id is required'),
      }),
    )
    .required('Pricing is required'),
  siteImages: yup
    .array()
    .of(
      yup.object({
        id: yup.string().optional(),
        imageUrl: yup.string().required(),
        type: yup
          .mixed<ESiteImagesTypes>()
          .oneOf(Object.values(ESiteImagesTypes))
          .required(),
      }),
    )
    .nullable(),
  partnerRedirectLink: yup.string().optional(),
  partnerUpgradeMessage: yup.string().optional(),
  chatTemplateColors: yup
    .object({
      options: yup
        .object({
          [EChatbotTheme.simple]: yup.array(yup.string()).optional(),
          [EChatbotTheme.linkView]: yup.object().optional(),
        })
        .default(undefined),
      selection: yup
        .object({
          theme: yup.string().required(),
          [EChatbotTheme.simple]: yup.string().required(),
          custom: yup.object({
            [EChatbotTheme.simple]: yup.boolean().required(),
            [EChatbotTheme.linkView]: yup.boolean().required(),
          }),
          [EChatbotTheme.linkView]: yup.string().required(),
        })
        .default(undefined),
    })
    .optional()
    .nullable(),
  allowedQueryParams: yup.string().optional(),
  legalName: yup.string().optional(),
  legalAddress: yup.string().optional(),
  stateOfIncorporation: yup.string().optional(),
})

export const generalSettingsPartnerValidationSchema = yup.object<TPartner>({
  partnerName: yup.string().required('Partner name is required'),
  domain: yup.string().required('Domain is required'),
  trackingEmail: yup.string().matches(EMAIL_REGEX, 'Invalid email'),
  collectionEmail: yup.string().matches(EMAIL_REGEX, 'Invalid email'),
  showBrandingToggle: yup.boolean().optional(),
  showCharacterToggle: yup.boolean().optional(),
  showEmbeddedChat: yup.boolean().optional(),
  defaultAgentName: yup.string().optional(),
  defaultService: yup.string().optional(),
  chatAgentId: yup.string().optional(),
  billingEmail: yup.string().matches(EMAIL_REGEX, 'Invalid email').optional(),
  supportEmail: yup.string().matches(EMAIL_REGEX, 'Invalid email').optional(),
  notificationEmail: yup
    .string()
    .matches(EMAIL_REGEX, 'Invalid email')
    .optional(),
  emailDomain: yup.string(),
  partnerType: yup
    .mixed<EPartnerType>()
    .oneOf(Object.values(EPartnerType))
    .optional(),
  partnerRedirectLink: yup.string().optional(),
  partnerUpgradeMessage: yup.string().optional(),
  productName: yup.string().optional(),
  demoLink: yup.string().optional(),
  customerAuthType: yup
    .mixed<ECustomerAuthType>()
    .oneOf(Object.values(ECustomerAuthType))
    .optional(),
  legalName: yup.string().optional(),
  legalAddress: yup.string().optional(),
  stateOfIncorporation: yup.string().optional(),
})

export const getDefaultBrandUIColors = (
  definedColors: TBrandColor[] | null,
) => {
  return convertPaletteObjectToBrandColors(DEFAULT_PALETTE).map((color) => {
    const existingColor = (definedColors || [])?.find(
      (c) => c.type === color.type,
    )

    if (existingColor) {
      return existingColor
    }

    return color
  })
}

export const getPartnerWithDefaultUndefinedProps = (partner: TPartner) => {
  const result: TPartner = { ...partner }

  Object.entries(result).forEach(([key, value]) => {
    if (!value) {
      result[key as keyof TPartner] = undefined as never
    }
    if (key === partnerKeys.brandUIColors) {
      result[key as keyof TPartner] = getDefaultBrandUIColors(
        value as TBrandColor[],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ) as never
    }
    if (key === partnerKeys.allowedQueryParams && Array.isArray(value)) {
      result[key as keyof TPartner] = getStringListFromArray(
        value as string[],
      ) as never
    }
  })

  return result
}

export const getInitialValuesGeneralSettingsPartner = (partner: TPartner) => {
  const generalSettingsValuesKeys = [
    'partnerName',
    'showBrandingToggle',
    'showCharacterToggle',
    'domain',
    'defaultAgentName',
    'defaultService',
    'showEmbeddedChat',
    'chatAgentId',
    'billingEmail',
    'supportEmail',
    'notificationEmail',
    'emailDomain',
    'partnerType',
    'id',
    'createdDate',
    'updatedDate',
    'partnerRedirectLink',
    'partnerUpgradeMessage',
    'trackingEmail',
    'collectionEmail',
    'productName',
    'demoLink',
    'allowedQueryParams',
    'customerAuthType',
    'legalName',
    'legalAddress',
    'stateOfIncorporation',
  ]

  const initialValues = {
    ...pick(partner, generalSettingsValuesKeys),
  }

  Object.entries(initialValues).forEach(([key, value]) => {
    if (!value) {
      initialValues[key as keyof TPartner] = undefined as never
    }
    if (key === partnerKeys.allowedQueryParams && Array.isArray(value)) {
      initialValues[key as keyof TPartner] = getStringListFromArray(
        value as string[],
      ) as never
    }
  })

  return initialValues as TPartnerGeneralSettingsForm
}
export const pricingCognitoGroups = [
  ECognitoGroups.customerBasicPlan,
  ECognitoGroups.customerPlusPlan,
]

export const getPricingItemByCognitoGroup = (
  formik: FormikProps<TPartner>,
  cognitoGroup: ECognitoGroups,
) => {
  const defaultItem = {
    tier: cognitoGroup,
    stripeProductId: '',
    stripePriceId: '',
  } as TPartnerPricing

  return (
    (formik.values.pricing || [])?.find(
      (pricing) => pricing.tier === cognitoGroup,
    ) || defaultItem
  )
}

export const getDefaultPricingArray = (formik: FormikProps<TPartner>) =>
  pricingCognitoGroups.map((group) =>
    getPricingItemByCognitoGroup(formik, group),
  )

export const getPricingKeyIndexByTier = (
  formik: FormikProps<TPartner>,
  cognitoGroup: ECognitoGroups,
) =>
  (formik.values.pricing || getDefaultPricingArray(formik)).findIndex(
    (pricing) => pricing.tier === cognitoGroup,
  )

export const getDefaultSiteImages = (
  existingSiteImages: TSiteImages[] | null,
) => {
  return Object.keys(ESiteImagesTypes).map(
    (key) =>
      ({
        type: key as ESiteImagesTypes,
        imageUrl:
          (existingSiteImages || []).find((image) => image.type === key)
            ?.imageUrl || '',
      }) as TSiteImages,
  )
}

export const getDefaultPartner = (partner?: TPartner | null) => {
  return {
    ...partner,
    partnerName: partner?.partnerName || '',
    showBrandingToggle: partner?.showBrandingToggle || false,
    showCharacterToggle: partner?.showCharacterToggle || false,
    domain: partner?.domain || '',
    defaultAgentName: partner?.defaultAgentName || '',
    defaultService: partner?.defaultService || '',
    brandUIColors: partner?.brandUIColors || null,
    customHeaderLinks: partner?.customHeaderLinks || null,
    socialLinks: partner?.socialLinks || null,
    showEmbeddedChat: partner?.showEmbeddedChat || false,
    chatAgentId: partner?.chatAgentId || '',
    chatTemplateColors: partner?.chatTemplateColors || null,
    billingEmail: partner?.billingEmail || '',
    supportEmail: partner?.supportEmail || '',
    notificationEmail: partner?.notificationEmail || '',
    emailDomain: partner?.emailDomain || '',
    partnerType: partner?.partnerType || EPartnerType.none,
    pricing: partner?.pricing || null,
    siteImages: partner?.siteImages || null,
    id: partner?.id || '',
    createdDate: partner?.createdDate || '',
    updatedDate: partner?.updatedDate || '',
    partnerRedirectLink: partner?.partnerRedirectLink || '',
    partnerUpgradeMessage: partner?.partnerUpgradeMessage || '',
    purchaseLink: partner?.purchaseLink || '',
    trackingEmail: partner?.trackingEmail || '',
    collectionEmail: partner?.collectionEmail || '',
    paymentMethodId: partner?.paymentMethodId || '',
    productName: partner?.productName || '',
    demoLink: partner?.demoLink || '',
    allowedQueryParams: partner?.allowedQueryParams || [],
    customerAuthType: partner?.customerAuthType || ECustomerAuthType.internal,
    legalName: partner?.legalName || '',
    legalAddress: partner?.legalAddress || '',
    stateOfIncorporation: partner?.stateOfIncorporation || '',
  } as TPartner
}

export const initialPartnerFormValues: TPartner = {
  ...getDefaultPartner(),
  pricing: getDefaultPricingArray({
    values: { pricing: null },
  } as FormikProps<TPartner>),
  brandUIColors: getDefaultBrandUIColors(null),
}
